import axios from 'axios';

class ApiService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001';
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 10000,
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshToken = localStorage.getItem('refreshToken');
            if (refreshToken) {
              const response = await this.refreshToken(refreshToken);
              const { access } = response.data;
              
              localStorage.setItem('accessToken', access);
              originalRequest.headers.Authorization = `Bearer ${access}`;
              
              return this.client(originalRequest);
            }
          } catch (refreshError) {
            // Refresh token failed, redirect to login
            localStorage.clear();
            window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Auth methods
  async login(credentials) {
    return this.client.post('/auth/login/', credentials);
  }

  async refreshToken(refreshToken) {
    return this.client.post('/auth/refresh/', { refreshToken });
  }

  async logout() {
    return this.client.post('/auth/logout/');
  }

  // Chat methods
  async sendMessage(payload) {
    return this.client.post('/chat/', payload);
  }

  async getChatHistory() {
    return this.client.get('/chat/history/');
  }

  async getConversations() {
    return this.client.get('/chat/conversation/');
  }

  async getConversationHistory(conversationId) {
    return this.client.get(`/chat/conversation/${conversationId}/`);
  }

  async summarizeConversation(conversationId) {
    return this.client.post(`/chat/summarize/${conversationId}/`);
  }

  async getSummaryStatus(conversationId) {
    return this.client.get(`/chat/summary-status/${conversationId}/`);
  }

  // User methods
  async getUserProfile() {
    return this.client.get('/user/profile/');
  }

  // Generic methods
  async get(url, config = {}) {
    return this.client.get(url, config);
  }

  async post(url, data = {}, config = {}) {
    return this.client.post(url, data, config);
  }

  async put(url, data = {}, config = {}) {
    return this.client.put(url, data, config);
  }

  async delete(url, config = {}) {
    return this.client.delete(url, config);
  }
}

const apiService = new ApiService();
export default apiService; 